import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Link, List, ListItem } from '@mui/material';
import { Box } from '@mui/system';
import Grid from '@mui/material/Unstable_Grid2';
import { AutocompleteElement, FormContainer, SelectElement, TextFieldElement } from 'react-hook-form-mui';

import useErrorHandling from '@components/common/hooks/useErrorHandling';
import CheckboxButtonGroup from '@components/common/HookFormMui/CheckboxButtonGroup';
import RadioButtonGroup from '@components/common/HookFormMui/RadioButtonGroup';
import RequiredFields from '@components/common/HookFormMui/RequiredFields';
import UserContext from '@lib/contexts/userContext';
import { AbilityContext, Subjects } from '@lib/permissions';
import { toUserOption } from '@lib/user';
import {
  sortByLabelWithOtherLast,
  getValuesFromAnswers,
  formatFormQuestions
} from '@lib/utils';

const EventForm = ({ form, event, questions, pearsData, staffUsers, availableCurriculum, submitting, onSubmit, valuesSetState }) => {
  const { userState: { userRecord  } } = useContext(UserContext);

  const [wasSubmitting, setWasSubmitting] = useState(false);
  // Whether to automatically fill the event name field based on the curriculum and partner site.
  const [autofillEventName, setAutofillEventName] = useState(true);
  const [valuesSet, setValuesSet] = valuesSetState;

  const ability = useContext(AbilityContext);
  const userCanAssign = ability.can('assign', Subjects.EVENT);

  const answers = useMemo(() => {
    if (!questions || !event.event_answers) {
      return {};
    }
    return getValuesFromAnswers(event.event_answers, questions) ?? {}
  }, [event.event_answers, questions]);

  const formQuestions = useMemo(() =>
      formatFormQuestions(questions, answers),
  [questions, answers]);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = form;

  const eventTypeWatch = watch('event_type');
  const curriculumWatch = watch('curriculum');

  const getUser = useCallback((email) => {
    const user = staffUsers.find((u) => u.email === email);
    if (user) {
      return user;
    }
    // User not in myINEP. Could be a former employee or other deleted user.
    return {
      email
    };
  }, [staffUsers]);

  /** ERROR HANDLING **/
  useErrorHandling({ errors, isSubmitting, wasSubmitting, setWasSubmitting });

  const curriculumOptions = useMemo(() => {
    // The Curriculum field shows currently active curricula. If editing a legacy event that belongs to
    // an inactive curriculum, that curriculum will also appear in the list.
    return availableCurriculum
      .filter(c => c.active || c.id === event?.curriculum_id)
      .map((item) => ({
        id: item.id,
        label: item.name,
        custom: item.custom,
      }))
      .sort(sortByLabelWithOtherLast);
  }, [availableCurriculum, event?.curriculum_id]);

  useEffect(() => {
    if (!valuesSet) {
      return;
    }
    // Clear selected curriculum if it's not in the list
    if (!curriculumWatch || !curriculumOptions.find((c) => c.id === curriculumWatch.id)) {
      setValue('curriculum', null);
    }
    // Clear curriculum_name if a non-custom curriculum is chosen
    if (!curriculumWatch?.custom) {
      setValue('curriculum_name', null);
    }
  }, [curriculumOptions, curriculumWatch, setValue, valuesSet]);

  useEffect(() => {
    if (event === false) {
      // No event means we are creating one
      if (!valuesSet) {
        setValue('created_by', toUserOption(getUser(userRecord?.email)));
        setValuesSet(true);
      }
      return;
    } else if (!event) {
      // Event was not found
      return;
    }

    // Editing existing event. Populate values into the form fields.
    const answers = getValuesFromAnswers(event.event_answers, questions) ?? {};

    const selectedCurriculum =  curriculumOptions.find(co => co.id === event.curriculum_id);

    const values = {
      points: event.points,
      created_by: toUserOption(getUser(event.created_by)),
      event_name: event.event_name,
      event_partner: event.event_partner,
      main_office: event.main_office,
      is_test: event.is_test ? 'true' : 'false',
      event_type: event.event_type,
      curriculum: event.curriculum_id ? selectedCurriculum : null,
      curriculum_name: event?.curriculum_name,

      ...answers,
    };
    Object.keys(values).forEach((key) => setValue(key, values[key]));
    setValuesSet(true);

    // For existing events, don't auto-update the event_name field.
    setAutofillEventName(false);

  }, [event, getUser, setValue, setValuesSet, staffUsers, userRecord?.email, valuesSet, curriculumOptions, questions]);

  const handleEventTypeChange = async (value) => {
    if (!valuesSet) {
      return;
    }
    if (value === 'BOOTH') {
      setValue('curriculum', '');
    }
  };

  const updateEventName = useCallback(() => {
    if (!valuesSet) {
      return;
    }
    // If editing an existing event, or the user manually edited the value in the event_name field,
    // don't auto-update the event name.
    if (!autofillEventName) {
      return;
    }

    let curriculum = getValues('curriculum');
    let curriculum_name = '';
    if (curriculum?.custom) {
      curriculum_name = getValues('curriculum_name') || curriculum?.label;
    } else {
      curriculum_name = curriculum?.label || curriculum;
    }
    let partner = getValues('event_partner');
    partner = partner?.label || partner;
    const args = [];
    if (curriculum_name) {
      args.push(curriculum_name.trim());
    }
    if (eventTypeWatch === 'BOOTH') {
      args.push('Booth');
    }
    if (partner) {
      args.push(partner.trim());
    }
    if (args.length === 2) {
      setValue('event_name', args.join(' - '));
    }
  }, [autofillEventName, eventTypeWatch, getValues, setValue, valuesSet]);

  // Update event name when event type changes (booths only)
  // Note: This is not part of handleEventTypeChange because that runs before the value changes.
  useEffect(() => {
    if (eventTypeWatch === 'BOOTH') {
      updateEventName();
    }
  }, [eventTypeWatch, getValues, setValue, updateEventName, valuesSet]);

  /**
   * Update the event name when the curriculum option or custom curriculum name changes.
   */
  const handleCurriculumChange = (_event, _value) => {
    updateEventName();
  };

  const handlePartnerChange = () => {
    if (!valuesSet) {
      return;
    }
    updateEventName();
  };

  const getFormField = (field) => (
    <ListItem sx={{ display: "list-item" }} key={`field-${field.props.id}`}>
      { field }
    </ListItem>
  );

  const fields = [];

  fields.push(
    <RadioButtonGroup
      id="field-is_test"
      name="is_test"
      label={'What is this event being created for?'}
      placeholder={'Select event purpose'}
      control={control}
      fullWidth
      required
      options={[
        { label: 'Community Event', id: 'false' },
        { label: 'Test Event', id: 'true' },
      ]}
    />
  );

  fields.push(
    <SelectElement
      label={formQuestions?.project?.label}
      id="field-project"
      name="project"
      variant="outlined"
      control={control}
      required
      fullWidth
      options={formQuestions?.project?.options ?? []}
    />
  );

  fields.push(
    <RadioButtonGroup
      id="field-event_type"
      name="event_type"
      label={'What type of event is this?'}
      placeholder={'Select event type'}
      control={control}
      onChange={handleEventTypeChange}
      fullWidth
      required
      options={[
        { label: 'Direct Education Class(es)', id: 'CLASS' },
        { label: 'Booth', id: 'BOOTH' },
      ]}
    />
  );

  if (eventTypeWatch === 'CLASS') {
    fields.push(
      <AutocompleteElement
        id="field-curriculum"
        name="curriculum"
        label={'What is the primary curriculum for this event?'}
        placeholder={'Choose curriculum'}
        control={control}
        fullWidth
        required
        options={curriculumOptions || []}
        autocompleteProps={{
          onChange: handleCurriculumChange,
          isOptionEqualToValue: (option, newValue) => {
            return option?.id === newValue?.id;
          }
      }}
      />
    );

    if (curriculumWatch?.custom) {
      fields.push(
        <TextFieldElement
          id="field-curriculum_name"
          name="curriculum_name"
          label={'Please enter the primary curriculum'}
          control={control}
          required
          fullWidth
          onChange={handleCurriculumChange}
        />
      );
    }

  }

  /**
   * PEARS field
   */
  fields.push(
    <AutocompleteElement
      id="field-event_partner"
      name="event_partner"
      label={'Which partner are you coordinating this event with?'}
      textFieldProps={{
        helperText: <>If you cannot find a site, please email <Link href="mailto:uie-pears-notifications@illinois.edu">uie-pears-notifications@illinois.edu</Link> to request to add the site.</>
      }}
      control={control}
      fullWidth
      required
      options={pearsData?.partners || []}
      autocompleteProps={{
        onChange: handlePartnerChange,
        isOptionEqualToValue: (option, newValue) => {
          return option === newValue;
        }
      }}
    />
  );

  fields.push(
    <CheckboxButtonGroup
      label={formQuestions?.participant_needs?.label}
      id="field-participant_needs"
      name="participant_needs"
      control={control}
      maxColumns={2}
      required
      fullWidth
      textFieldRequired
      options={formQuestions?.participant_needs?.options ?? []}
    />
  );

  /**
   * PEARS field
   */
  fields.push(
    <AutocompleteElement
      id="field-main_office"
      name="main_office"
      label={'What is your main office?'}
      placeholder={'Choose main office'}
      control={control}
      fullWidth
      required
      options={pearsData?.main_offices || []}
      autocompleteProps={{
        isOptionEqualToValue: (option, newValue) => {
          return option === newValue;
        }
      }}
    />
  );

  /**
   * myINEP field
   */
  fields.push(
    <AutocompleteElement
      id="field-created_by"
      name="created_by"
      label={'Created By'}
      helperText={'Name and email address of the user assigned to this event.'}
      placeholder={'Choose assignee'}
      control={control}
      fullWidth
      required
      options={(staffUsers ? staffUsers.map(toUserOption) : [])}
      autocompleteProps={{
        readOnly: !userCanAssign,
        isOptionEqualToValue: (option, newValue) => {
          return option?.id === newValue?.id;
        }
      }}
    />
  );

  /**
   * Autogenerated at first
   */
  fields.push(
    <TextFieldElement
      id="field-event_name"
      name="event_name"
      label={'Event Name'}
      control={control}
      required
      fullWidth
      onChange={() => setAutofillEventName(false)}
    />
  );

  return (
    <Box width={'100%'}>
      { !!valuesSet && (
        <FormContainer onSuccess={handleSubmit(onSubmit)} FormProps={{ className: 'mui' }}>
          <RequiredFields />
          <Grid container columns={12} spacing={2}>
            <Grid xs={12} sm={10} md={8}>
              <List sx={{
                  listStyle: 'decimal',
                  paddingLeft: 2,
                }}>
                { fields.map(getFormField) }
              </List>
            </Grid>

            <Grid xs={12} sm={10} md={8}>
              <Button
                id="button-submit"
                disabled={submitting}
                type={'submit'}
                variant="contained"
                size="small"
                sx={{ marginTop: '20px' }}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </FormContainer>
      )}
    </Box>
  );
};

export default EventForm;
